import { useState } from 'react';
import { Table, Form, Button, Alert, Card } from 'react-bootstrap';

import Loader from '../components/Loader';
import Message from '../components/Message';
import BackToTop from '../components/BackToTop';

import {
  useClaimBenchmarksMutation,
  useGetMySystemsDetailQuery,
} from '../slices/benchmarksApiSlice';

export default function MySystemsScreen() {
  const [systemIdInput, setSystemIdInput] = useState('');
  const [claimMessage, setClaimMessage] = useState(null);
  const [claimError, setClaimError] = useState(null);

  const { data, isLoading, error } = useGetMySystemsDetailQuery();
  const [claimBenchmarks, { isLoading: isClaiming }] = useClaimBenchmarksMutation();

  const handleClaim = async (e) => {
    e.preventDefault();
    setClaimMessage(null);
    setClaimError(null);

    try {
      const result = await claimBenchmarks({ systemId: systemIdInput }).unwrap();
      setClaimMessage(result.message);
      setSystemIdInput('');
    } catch (err) {
      setClaimError(err?.data?.message || 'Failed to claim benchmarks');
    }
  };

  return (
    <>
      <h1 style={{ marginTop: '32px' }}>My Systems</h1>

      {/* Claim Section */}
      <Card className='card--opaque mb-4 shadow-sm'>
        <Card.Body>
          <h5 className='mb-3'>Claim a System</h5>
          <p className='text-muted mb-3'>
            Find your System ID in the JDiskMark desktop app's About dialog
            (Help → About). Paste it below to claim all benchmarks submitted
            from that machine.
          </p>

          {claimMessage && <Alert variant='success'>{claimMessage}</Alert>}
          {claimError && <Alert variant='danger'>{claimError}</Alert>}

          <Form onSubmit={handleClaim} className='d-flex gap-2'>
            <Form.Control
              type='text'
              placeholder='Paste your 32-character System ID'
              value={systemIdInput}
              onChange={(e) => setSystemIdInput(e.target.value)}
              maxLength={32}
              style={{ fontFamily: 'monospace' }}
            />
            <Button
              type='submit'
              variant='primary'
              disabled={isClaiming || systemIdInput.trim().length !== 32}
            >
              {isClaiming ? 'Claiming…' : 'Claim'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Systems Table */}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>
          {error?.data?.message || error.error}
        </Message>
      ) : !data?.systems?.length ? (
        <Message variant='info'>
          No systems found. Use the form above to claim a system.
        </Message>
      ) : (
        <Table striped hover responsive className='table-sm'>
          <thead>
            <tr>
              <th>System ID</th>
              <th>OS</th>
              <th>Processor</th>
              <th>Arch</th>
              <th>Drives</th>
              <th className='text-center'>Benchmarks</th>
              <th className='text-end'>Last Benchmark</th>
            </tr>
          </thead>
          <tbody>
            {data.systems.map((sys) => (
              <tr key={sys.systemId}>
                <td>
                  <code
                    title={sys.systemId}
                    style={{ fontSize: '0.85rem' }}
                  >
                    {sys.systemId?.substring(0, 8)}…
                  </code>
                </td>
                <td>{sys.os || '—'}</td>
                <td>{sys.processorName || '—'}</td>
                <td>{sys.arch || '—'}</td>
                <td>
                  {sys.driveModels?.length > 0
                    ? sys.driveModels.join(', ')
                    : '—'}
                </td>
                <td className='text-center'>{sys.benchmarkCount}</td>
                <td className='text-end'>
                  {sys.lastBenchmark
                    ? new Date(sys.lastBenchmark).toLocaleDateString()
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <BackToTop heroThreshold={200} />
    </>
  );
}
